/**
 * Parsing e formatting date condivisi.
 *
 * Il backend AppSync espone le date in formato italiano `DD/MM/YYYY`
 * (es. `"31/01/2026"`), NON in ISO. Il nativo `new Date("31/01/2026")`
 * o `new Date("2026-01-31T00:00:00")` non sono interpretati uniformemente
 * → fallback a `Invalid Date`. Centralizziamo qui il parsing con `dayjs`.
 *
 * Il mobile usa lo stesso pattern: `dayjs(value, 'DD/MM/YYYY')`.
 */

import dayjs from 'dayjs'
import 'dayjs/locale/it'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)
dayjs.locale('it')

const BACKEND_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY']

/**
 * Parsa una data del backend. Ritorna un dayjs oggetto valido o `null`.
 */
export function parseBackendDate(value?: string | null) {
  if (!value) return null
  const d = dayjs(value, BACKEND_FORMATS, true)
  return d.isValid() ? d : null
}

/**
 * Formatta una data backend per la UI in formato breve (es. "31 gen 2026").
 * Se la data non è valida, ritorna il fallback (default `—`).
 */
export function formatBackendDate(value?: string | null, fallback = '—'): string {
  const d = parseBackendDate(value)
  if (!d) return fallback
  return d.format('DD MMM YYYY')
}

/**
 * Formatta un periodo "dal–al" usando lo stesso parser.
 * Se è nello stesso mese ritorna un'unica label (es. "gennaio 2026"),
 * altrimenti due (es. "dic 2025 – gen 2026").
 */
export function formatBackendPeriodRange(
  dal?: string | null,
  al?: string | null,
  emissione?: string | null,
): string {
  const dDal = parseBackendDate(dal)
  const dAl = parseBackendDate(al)

  if (dDal && dAl) {
    const sameMonth = dDal.isSame(dAl, 'month')
    if (sameMonth) return dDal.format('MMMM YYYY')
    return `${dDal.format('MMM YYYY')} – ${dAl.format('MMM YYYY')}`
  }

  const dEm = parseBackendDate(emissione)
  if (dEm) return dEm.format('MMMM YYYY')

  return '—'
}
